import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import usePermissions from '../../hooks/usePermissions';
import LeaguePlayerPicker from './LeaguePlayerPicker';
import './Leagues.css';

/**
 * One fixture in the admin list with inline result/link controls.
 *
 * Keeps its own draft input state so typing in one row never re-renders the
 * others. A fixture whose points are awarded is read-only (shows the score); an
 * un-recorded one offers either a manual score entry or coupling to a played
 * match by id.
 *
 * @param {object} props - Component props.
 * @param {object} props.fixture - Serialized LeagueMatch row.
 * @param {Function} props.onResult - ``(fixture, home, away)`` records a score.
 * @param {Function} props.onLink - ``(fixture, matchId)`` couples a played match.
 * @param {Function} props.onUnlink - ``(fixture)`` detaches a coupled match.
 * @param {Function} props.onDelete - ``(fixture)`` removes an un-played fixture.
 * @param {Function} props.onForfeit - ``(fixture, 'home'|'away')`` awards a walkover.
 */
function FixtureRow({ fixture, onResult, onLink, onUnlink, onDelete, onForfeit }) {
  const { t } = useTranslation();
  const [home, setHome] = useState('');
  const [away, setAway] = useState('');
  const [matchId, setMatchId] = useState('');
  const [busy, setBusy] = useState(false);

  const recorded = fixture.points_awarded;
  const linked = Boolean(fixture.match_id);

  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixture-row">
      <div className="fixture-teams">
        <span className="fixture-team">{fixture.home_name}</span>
        <span className="fixture-vs">
          {recorded ? `${fixture.home_score}–${fixture.away_score}` : 'vs'}
        </span>
        <span className="fixture-team">{fixture.away_name}</span>
      </div>
      <div className="fixture-meta">
        <span className={`league-season-pill ${recorded ? 'is-finished' : 'is-upcoming'}`}>
          {fixture.status_display || fixture.status}
        </span>
        {fixture.division_name && <span className="fixture-div">{fixture.division_name}</span>}
        {linked && <span className="fixture-div">{t('leagues.linkedMatch', { id: fixture.match_id })}</span>}
        {fixture.is_forfeit && <span className="fixture-wo-pill" title="Walkover">W/O</span>}
      </div>
      {!recorded && (
        <div className="fixture-actions">
          {linked ? (
            <button className="btn-subtle" disabled={busy} onClick={() => run(() => onUnlink(fixture))}>
              {t('leagues.unlink')}
            </button>
          ) : (
            <>
              <div className="fixture-score-inputs">
                <input
                  className="score-input"
                  type="number"
                  min="0"
                  value={home}
                  onChange={(e) => setHome(e.target.value)}
                  placeholder={t('leagues.homeAbbr')}
                />
                <span className="fixture-dash">–</span>
                <input
                  className="score-input"
                  type="number"
                  min="0"
                  value={away}
                  onChange={(e) => setAway(e.target.value)}
                  placeholder={t('leagues.awayAbbr')}
                />
                <button
                  className="btn-primary-sm"
                  disabled={busy || home === '' || away === ''}
                  onClick={() => run(() => onResult(fixture, home, away))}
                >
                  {t('settings.save')}
                </button>
              </div>
              <div className="fixture-link-inputs">
                <input
                  className="score-input score-input-wide"
                  value={matchId}
                  onChange={(e) => setMatchId(e.target.value)}
                  placeholder="Match-ID"
                />
                <button
                  className="btn-subtle"
                  disabled={busy || !matchId}
                  onClick={() => run(() => onLink(fixture, matchId))}
                >
                  {t('leagues.link')}
                </button>
              </div>
              <div className="fixture-forfeit">
                <span className="fixture-wo-label">W/O:</span>
                <button
                  className="btn-subtle"
                  disabled={busy}
                  title={t('leagues.walkoverWinTo', { name: fixture.home_name })}
                  onClick={() => run(() => onForfeit(fixture, 'home'))}
                >
                  {fixture.home_name}
                </button>
                <button
                  className="btn-subtle"
                  disabled={busy}
                  title={t('leagues.walkoverWinTo', { name: fixture.away_name })}
                  onClick={() => run(() => onForfeit(fixture, 'away'))}
                >
                  {fixture.away_name}
                </button>
              </div>
              <button
                className="btn-subtle fixture-delete"
                disabled={busy}
                title={t('leagues.deleteFixtureTitle')}
                onClick={() => run(() => onDelete(fixture))}
              >
                {t('comments.delete')}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Admin drift panel shown in the Administration tab for users with ``canManageLeagues``.
 *
 * Drives the league lifecycle the read views can't: activate/close the season,
 * create new seasons, register participants (players or persistent teams), and
 * manage fixtures (create, record a result, or couple/decouple a played CS2
 * match). All writes forward the caller's JWT through the stats proxy, which the
 * Django API authorises as an admin.
 *
 * @param {object} props - Component props.
 * @param {object} props.league - The league being administered.
 * @param {Array<object>} props.seasons - The league's season summaries.
 * @param {string} props.seasonId - The currently selected season id.
 * @param {object} props.standings - Standings payload (divisions + participants).
 * @param {Function} props.onSeasonsChanged - ``(selectId?)`` reloads seasons.
 * @param {Function} props.onStandingsChanged - Reloads the current standings.
 * @param {Function} props.onLeagueChanged - Reloads the league after a settings edit.
 */
export default function LeagueAdminPanel({
  league,
  seasons,
  seasonId,
  standings,
  onSeasonsChanged,
  onStandingsChanged,
  onLeagueChanged,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const { guildId } = usePermissions();

  const [fixtures, setFixtures] = useState([]);
  const [teams, setTeams] = useState([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // New-season form.
  const [seasonOpen, setSeasonOpen] = useState(false);
  const [nsDivisions, setNsDivisions] = useState(1);
  const [nsCapacity, setNsCapacity] = useState('');
  const [nsPromote, setNsPromote] = useState(2);
  const [nsRelegate, setNsRelegate] = useState(2);

  // League-settings edit form (seeded from the league; game_mode is fixed).
  const [leagueEditOpen, setLeagueEditOpen] = useState(false);
  const [lgName, setLgName] = useState('');
  const [lgDesc, setLgDesc] = useState('');
  const [lgPw, setLgPw] = useState(3);
  const [lgPd, setLgPd] = useState(1);
  const [lgPl, setLgPl] = useState(0);
  const [lgMatchType, setLgMatchType] = useState('BO1');
  const [lgActive, setLgActive] = useState(true);
  const [lgLockRosters, setLgLockRosters] = useState(false);
  const [lgMinGames, setLgMinGames] = useState(0);

  // Season-settings edit form (seeded from the selected season).
  const [seasonEditOpen, setSeasonEditOpen] = useState(false);
  const [seName, setSeName] = useState('');
  const [sePromote, setSePromote] = useState(2);
  const [seRelegate, setSeRelegate] = useState(2);

  // Division-structure edit form (upcoming seasons only; shared capacity).
  const [divEditOpen, setDivEditOpen] = useState(false);
  const [edDivisions, setEdDivisions] = useState(1);
  const [edCapacity, setEdCapacity] = useState('');
  const [edNames, setEdNames] = useState([]);

  // Registration form.
  const [regDivisionId, setRegDivisionId] = useState('');
  const [regPlayers, setRegPlayers] = useState([]);
  const [regTeamId, setRegTeamId] = useState('');

  // Fixture-creation form.
  const [fxDivisionId, setFxDivisionId] = useState('');
  const [fxHomeId, setFxHomeId] = useState('');
  const [fxAwayId, setFxAwayId] = useState('');
  const [fxMeetings, setFxMeetings] = useState(2);

  const season = seasons.find((s) => s.id === seasonId) || null;
  const teamBased = Boolean(league.is_team_based);
  // Memoised so it keeps a stable identity across renders — several effects
  // below depend on it (and on a signature derived from it).
  const divisions = useMemo(() => standings?.divisions || [], [standings]);
  const fxParticipants = divisions.find((d) => d.id === fxDivisionId)?.participants || [];

  // Single-division leagues have no division dropdown, so default the fixture
  // pickers to that sole division once standings load.
  useEffect(() => {
    if (divisions.length === 1 && !fxDivisionId) setFxDivisionId(divisions[0].id);
  }, [divisions, fxDivisionId]);

  // Seed the league-settings form from the league (re-seeds after a save reload).
  useEffect(() => {
    setLgName(league.name || '');
    setLgDesc(league.description || '');
    setLgPw(league.points_win ?? 3);
    setLgPd(league.points_draw ?? 1);
    setLgPl(league.points_loss ?? 0);
    setLgMatchType(league.match_type || 'BO1');
    setLgActive(league.is_active !== false);
    setLgLockRosters(Boolean(league.lock_rosters));
    setLgMinGames(league.min_games_for_ranking ?? 0);
  }, [league]);

  // Seed the season-settings form from the selected season.
  useEffect(() => {
    setSeName(season?.name || '');
    setSePromote(season?.promote_count ?? 2);
    setSeRelegate(season?.relegate_count ?? 2);
  }, [season]);

  // Seed the division-structure form. Keyed on a signature of the divisions so
  // it re-seeds when switching season or after a save reloads them — but never
  // mid-edit (typing doesn't change the signature), so it won't clobber input.
  const divSignature = divisions.map((d) => `${d.id}:${d.capacity ?? ''}:${d.name}`).join(',');
  useEffect(() => {
    setEdDivisions(divisions.length || 1);
    setEdCapacity(divisions[0]?.capacity ?? '');
    setEdNames(divisions.map((d) => d.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasonId, divSignature]);

  // Keep the per-division name inputs in step with the chosen count: pad new
  // tiers with a sensible default, drop names for removed tiers, preserve the
  // rest. Skipped while the count field is mid-edit (empty / not a valid count).
  useEffect(() => {
    const n = Number(edDivisions);
    if (!Number.isInteger(n) || n < 1) return;
    setEdNames((prev) => {
      if (prev.length === n) return prev;
      const next = prev.slice(0, n);
      while (next.length < n) next.push(`Division ${next.length + 1}`);
      return next;
    });
  }, [edDivisions]);

  const apiWrite = useCallback(
    async (method, path, body) => {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const opts = { method, headers };
      if (body !== undefined) opts.body = JSON.stringify(body);
      const r = await fetch(`${API_URL}${path}`, opts);
      let data = null;
      try {
        data = await r.json();
      } catch {
        data = null;
      }
      return { ok: r.ok, status: r.status, data };
    },
    [getAuthHeaders],
  );

  const loadFixtures = useCallback(() => {
    if (!seasonId) {
      setFixtures([]);
      return;
    }
    fetch(`${API_URL}/api/leagues/seasons/${seasonId}/matches`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setFixtures(Array.isArray(d) ? d : []))
      .catch(() => setFixtures([]));
  }, [seasonId]);

  useEffect(() => {
    loadFixtures();
  }, [loadFixtures]);

  // Team leagues need the pool of persistent rosters for registration.
  useEffect(() => {
    if (!teamBased) return;
    fetch(`${API_URL}/api/leagues/teams?game_mode=${league.game_mode}&is_active=true`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setTeams(Array.isArray(d) ? d : []))
      .catch(() => setTeams([]));
  }, [teamBased, league.game_mode]);

  const flash = (msg) => {
    setNotice(msg);
    setError('');
  };
  const fail = (result, fallback) => {
    setError(result.data?.error || result.data?.detail || fallback);
    setNotice('');
  };

  const createSeason = async () => {
    const body = {
      num_divisions: Number(nsDivisions) || 1,
      promote_count: Number(nsPromote) || 0,
      relegate_count: Number(nsRelegate) || 0,
    };
    if (nsCapacity !== '') body.division_capacity = Number(nsCapacity);
    const res = await apiWrite('POST', `/api/leagues/${league.id}/seasons`, body);
    if (!res.ok) return fail(res, t('leagues.createSeasonFailed'));
    setSeasonOpen(false);
    flash(t('leagues.seasonCreated', { number: res.data.number }));
    onSeasonsChanged(res.data.id);
  };

  const activateSeason = async () => {
    const res = await apiWrite('PATCH', `/api/leagues/seasons/${seasonId}`, { status: 'active' });
    if (!res.ok) return fail(res, t('leagues.activateSeasonFailed'));
    flash(t('leagues.seasonActive'));
    onSeasonsChanged(seasonId);
  };

  const closeSeason = async () => {
    if (!window.confirm(t('leagues.closeSeasonConfirm'))) return;
    let res = await apiWrite('POST', `/api/leagues/seasons/${seasonId}/close`, {});
    // The API refuses (409) while fixtures are unplayed — offer to force-close,
    // which cancels the remaining fixtures.
    if (res.status === 409 && res.data?.total != null) {
      const { played, total } = res.data;
      if (!window.confirm(t('leagues.closeSeasonForceConfirm', { played, total }))) {
        return;
      }
      res = await apiWrite('POST', `/api/leagues/seasons/${seasonId}/close`, { force: true });
    }
    if (!res.ok) return fail(res, t('leagues.closeSeasonFailed'));
    flash(t('leagues.seasonClosed'));
    onSeasonsChanged(res.data.id);
  };

  const createPlayoff = async () => {
    if (!window.confirm(t('leagues.createPlayoffConfirm'))) return;
    const res = await apiWrite('POST', `/api/leagues/seasons/${seasonId}/create-playoff`, {});
    if (!res.ok) return fail(res, t('leagues.createPlayoffFailed'));
    flash(t('leagues.playoffCreated'));
    if (res.data?.tournament_id) navigate(`/tournaments/${res.data.tournament_id}`);
    else onSeasonsChanged(seasonId);
  };

  const deleteSeason = async () => {
    if (!window.confirm(t('leagues.deleteSeasonConfirm'))) return;
    let res = await apiWrite('DELETE', `/api/leagues/seasons/${seasonId}`);
    if (res.status === 409) {
      if (!window.confirm(t('leagues.deleteSeasonForceConfirm'))) return;
      res = await apiWrite('DELETE', `/api/leagues/seasons/${seasonId}?force=1`);
    }
    if (!res.ok) return fail(res, t('leagues.deleteSeasonFailed'));
    flash(t('leagues.seasonDeleted'));
    onSeasonsChanged();  // LeagueDetail re-selects a remaining season
  };

  const deleteLeague = async () => {
    if (!window.confirm(t('leagues.deleteLeagueConfirm', { name: league.name }))) return;
    let res = await apiWrite('DELETE', `/api/leagues/${league.id}`);
    if (res.status === 409) {
      if (!window.confirm(t('leagues.deleteLeagueForceConfirm'))) return;
      res = await apiWrite('DELETE', `/api/leagues/${league.id}?force=1`);
    }
    if (!res.ok) return fail(res, t('leagues.deleteLeagueFailed'));
    navigate('/leagues');
  };

  const saveLeagueSettings = async () => {
    if (!lgName.trim()) return setError(t('leagues.leagueNameEmpty'));
    const res = await apiWrite('PATCH', `/api/leagues/${league.id}`, {
      name: lgName.trim(),
      description: lgDesc,
      match_type: lgMatchType,
      points_win: Number(lgPw) || 0,
      points_draw: Number(lgPd) || 0,
      points_loss: Number(lgPl) || 0,
      lock_rosters: lgLockRosters,
      min_games_for_ranking: Number(lgMinGames) || 0,
      is_active: lgActive,
    });
    if (!res.ok) return fail(res, t('leagues.saveLeagueFailed'));
    setLeagueEditOpen(false);
    flash(t('leagues.leagueSaved'));
    if (onLeagueChanged) onLeagueChanged();
    onStandingsChanged();  // points-rule changes re-rank non-finished seasons
  };

  const saveSeasonSettings = async () => {
    if (!seName.trim()) return setError(t('leagues.seasonNameEmpty'));
    const body = { name: seName.trim() };
    // Promotion rules are locked once a season is finished.
    if (season.status !== 'finished') {
      body.promote_count = Number(sePromote) || 0;
      body.relegate_count = Number(seRelegate) || 0;
    }
    const res = await apiWrite('PATCH', `/api/leagues/seasons/${seasonId}`, body);
    if (!res.ok) return fail(res, t('leagues.saveSeasonFailed'));
    setSeasonEditOpen(false);
    flash(t('leagues.seasonSaved'));
    onSeasonsChanged(seasonId);
  };

  const saveDivisions = async () => {
    const target = Number(edDivisions) || 1;
    if (target < 1) return setError(t('leagues.minOneDivision'));
    // Removing the bottom tier(s) lifts their participants up one division —
    // confirm and surface how many are affected before shrinking.
    const current = divisions.length;
    if (target < current) {
      const lifted = divisions
        .slice(target)
        .reduce((sum, d) => sum + (d.participants?.length || 0), 0);
      const removed = current - target;
      const base = removed === 1
        ? t('leagues.removeDivisionsOne', { count: removed })
        : t('leagues.removeDivisionsMany', { count: removed });
      const msg = lifted > 0
        ? `${base} ${t('leagues.removeDivisionsLifted', { lifted })}`
        : base;
      if (!window.confirm(msg)) return;
    }
    const body = {
      num_divisions: target,
      division_capacity: edCapacity === '' ? null : Number(edCapacity),
      division_names: edNames.slice(0, target).map((n) => (n || '').trim()),
    };
    const res = await apiWrite('PATCH', `/api/leagues/seasons/${seasonId}/divisions`, body);
    if (!res.ok) return fail(res, t('leagues.updateDivisionsFailed'));
    setDivEditOpen(false);
    flash(t('leagues.divisionsUpdated'));
    onStandingsChanged();        // divisions changed → re-fetch standings
    onSeasonsChanged(seasonId);  // refresh the season summary (division_count)
  };

  const registerParticipant = async () => {
    const body = {};
    if (regDivisionId) body.division_id = regDivisionId;
    if (teamBased) {
      if (!regTeamId) return setError(t('leagues.selectTeamToRegister'));
      body.team_id = regTeamId;
    } else {
      const player = regPlayers[0];
      if (!player) return setError(t('leagues.selectPlayerToRegister'));
      body.player_id = player.player_id;
    }
    const res = await apiWrite('POST', `/api/leagues/seasons/${seasonId}/register`, body);
    if (!res.ok) return fail(res, t('leagues.registerFailed'));
    flash(t('leagues.participantRegistered'));
    setRegPlayers([]);
    setRegTeamId('');
    onStandingsChanged();
  };

  const createFixture = async () => {
    if (!fxHomeId || !fxAwayId) return setError(t('leagues.selectBothSides'));
    if (fxHomeId === fxAwayId) return setError(t('leagues.sidesMustDiffer'));
    const res = await apiWrite('POST', `/api/leagues/seasons/${seasonId}/matches`, {
      home_id: fxHomeId,
      away_id: fxAwayId,
      division_id: fxDivisionId || undefined,
    });
    if (!res.ok) return fail(res, t('leagues.createFixtureFailed'));
    flash(t('leagues.fixtureCreated'));
    setFxHomeId('');
    setFxAwayId('');
    loadFixtures();
  };

  const generateFixtures = async () => {
    const hasFixtures = fixtures.length > 0;
    if (hasFixtures && !window.confirm(t('leagues.regenerateConfirm'))) {
      return;
    }
    const res = await apiWrite('POST', `/api/leagues/seasons/${seasonId}/generate-fixtures`, {
      meetings: Number(fxMeetings) || 2,
      replace: hasFixtures,
    });
    if (!res.ok) return fail(res, t('leagues.generateFailed'));
    flash(t('leagues.fixturesCreated', { count: res.data.created }));
    loadFixtures();
  };

  const recordResult = async (fixture, homeScore, awayScore) => {
    const res = await apiWrite('POST', `/api/leagues/matches/${fixture.id}`, {
      home_score: Number(homeScore),
      away_score: Number(awayScore),
    });
    if (!res.ok) return fail(res, t('leagues.saveResultFailed'));
    flash(t('leagues.resultSaved'));
    loadFixtures();
    onStandingsChanged();
  };

  const linkMatch = async (fixture, matchId) => {
    const res = await apiWrite('POST', `/api/leagues/matches/${fixture.id}/link`, {
      match_id: Number(matchId),
    });
    if (!res.ok) return fail(res, t('leagues.linkFailed'));
    flash(t('leagues.matchLinked'));
    loadFixtures();
    onStandingsChanged();
  };

  const unlinkMatch = async (fixture) => {
    const res = await apiWrite('DELETE', `/api/leagues/matches/${fixture.id}/link`);
    if (!res.ok) return fail(res, t('leagues.unlinkFailed'));
    flash(t('leagues.matchUnlinked'));
    loadFixtures();
  };

  const deleteFixture = async (fixture) => {
    if (!window.confirm(t('leagues.deleteFixtureConfirm'))) return;
    const res = await apiWrite('DELETE', `/api/leagues/matches/${fixture.id}`);
    if (!res.ok) return fail(res, t('leagues.deleteFixtureFailed'));
    flash(t('leagues.fixtureDeleted'));
    loadFixtures();
  };

  const removeParticipant = async (participant) => {
    if (!window.confirm(t('leagues.removeParticipantConfirm', { name: participant.display_name }))) return;
    const res = await apiWrite(
      'DELETE', `/api/leagues/seasons/${seasonId}/participants/${participant.id}`,
    );
    if (!res.ok) return fail(res, t('leagues.removeParticipantFailed'));
    flash(t('leagues.participantRemoved'));
    loadFixtures();
    onStandingsChanged();
  };

  const withdrawParticipant = async (participant) => {
    if (!window.confirm(t('leagues.withdrawConfirm', { name: participant.display_name }))) return;
    const res = await apiWrite(
      'POST', `/api/leagues/seasons/${seasonId}/participants/${participant.id}/withdraw`, {},
    );
    if (!res.ok) return fail(res, t('leagues.withdrawFailed'));
    flash(t('leagues.participantWithdrawn', { count: res.data.forfeited }));
    loadFixtures();
    onStandingsChanged();
  };

  const forfeitFixture = async (fixture, winner) => {
    const name = winner === 'home' ? fixture.home_name : fixture.away_name;
    if (!window.confirm(t('leagues.forfeitConfirm', { name }))) return;
    const res = await apiWrite('POST', `/api/leagues/matches/${fixture.id}/forfeit`, { winner });
    if (!res.ok) return fail(res, t('leagues.forfeitFailed'));
    flash(t('leagues.forfeitRecorded'));
    loadFixtures();
    onStandingsChanged();
  };

  const playerName = (p) => p.steam?.username || p.username || p.player_id;
  const allParticipants = divisions.flatMap((d) => d.participants);
  const registeredTeamIds = new Set(
    divisions.flatMap((d) => d.participants.map((p) => p.team?.id).filter(Boolean)),
  );
  const completedCount = fixtures.filter((f) => f.points_awarded).length;

  return (
    <div className="league-admin">
      <div className="league-admin-head">
        <h2 className="league-admin-title">{t('leagues.tabAdmin')}</h2>
        {season && (
          <span className={`league-season-pill ${
            season.status === 'active' ? 'is-active'
              : season.status === 'finished' ? 'is-finished' : 'is-upcoming'
          }`}>
            {season.status_display || season.status}
          </span>
        )}
      </div>

      {error && <div className="league-form-error">{error}</div>}
      {notice && <div className="league-form-notice">{notice}</div>}

      {/* Settings: edit the league and the selected season */}
      <section className="admin-section">
        <h3 className="admin-section-title">{t('settings.title')}</h3>
        <div className="admin-actions">
          <button className="btn-subtle" onClick={() => setLeagueEditOpen((v) => !v)}>
            {leagueEditOpen ? t('matchDetail.cancel') : t('leagues.editLeague')}
          </button>
          {season && (
            <button className="btn-subtle" onClick={() => setSeasonEditOpen((v) => !v)}>
              {seasonEditOpen ? t('matchDetail.cancel') : t('leagues.editSeason')}
            </button>
          )}
        </div>

        {leagueEditOpen && (
          <div className="admin-form-grid">
            <label className="form-field form-field-wide">
              <span className="form-label">{t('leagues.nameField')}</span>
              <input className="form-input" value={lgName} onChange={(e) => setLgName(e.target.value)} />
            </label>
            <label className="form-field form-field-wide">
              <span className="form-label">{t('leagues.description')}</span>
              <input className="form-input" value={lgDesc} onChange={(e) => setLgDesc(e.target.value)} />
            </label>
            <label className="form-field">
              <span className="form-label">{t('leagues.matchLengthLabel')}</span>
              <select className="form-input" value={lgMatchType} onChange={(e) => setLgMatchType(e.target.value)}>
                <option value="BO1">{t('createMatch.bestOf1')}</option>
                <option value="BO3">{t('createMatch.bestOf3')}</option>
                <option value="BO5">{t('createMatch.bestOf5')}</option>
              </select>
            </label>
            <label className="form-field">
              <span className="form-label">{t('leagues.pointsWin')}</span>
              <input className="form-input" type="number" value={lgPw}
                onChange={(e) => setLgPw(e.target.value)} />
            </label>
            <label className="form-field">
              <span className="form-label">{t('leagues.pointsDraw')}</span>
              <input className="form-input" type="number" value={lgPd}
                onChange={(e) => setLgPd(e.target.value)} />
            </label>
            <label className="form-field">
              <span className="form-label">{t('leagues.pointsLoss')}</span>
              <input className="form-input" type="number" value={lgPl}
                onChange={(e) => setLgPl(e.target.value)} />
            </label>
            <label className="form-field form-field-check">
              <input type="checkbox" checked={lgActive} onChange={(e) => setLgActive(e.target.checked)} />
              <span className="form-label">{t('leagues.activeLeague')}</span>
            </label>
            <label className="form-field form-field-check">
              <input type="checkbox" checked={lgLockRosters} onChange={(e) => setLgLockRosters(e.target.checked)} />
              <span className="form-label">{t('leagues.lockRosters')}</span>
            </label>
            <label className="form-field">
              <span className="form-label">{t('leagues.minGames')}</span>
              <input className="form-input" type="number" min="0" value={lgMinGames}
                onChange={(e) => setLgMinGames(e.target.value)} />
            </label>
            <button className="btn-primary-sm admin-form-submit" onClick={saveLeagueSettings}>
              {t('leagues.saveLeague')}
            </button>
            <button className="btn-subtle fixture-delete admin-form-submit" onClick={deleteLeague}>
              {t('leagues.deleteLeague')}
            </button>
          </div>
        )}

        {seasonEditOpen && season && (
          <div className="admin-form-grid">
            <label className="form-field form-field-wide">
              <span className="form-label">{t('leagues.seasonName')}</span>
              <input className="form-input" value={seName} onChange={(e) => setSeName(e.target.value)} />
            </label>
            <label className="form-field">
              <span className="form-label">{t('leagues.promotion')}</span>
              <input className="form-input" type="number" min="0" value={sePromote}
                disabled={season.status === 'finished'}
                onChange={(e) => setSePromote(e.target.value)} />
            </label>
            <label className="form-field">
              <span className="form-label">{t('leagues.relegation')}</span>
              <input className="form-input" type="number" min="0" value={seRelegate}
                disabled={season.status === 'finished'}
                onChange={(e) => setSeRelegate(e.target.value)} />
            </label>
            {season.status === 'finished' && (
              <span className="form-hint">{t('leagues.promotionLocked')}</span>
            )}
            <button className="btn-primary-sm admin-form-submit" onClick={saveSeasonSettings}>
              {t('leagues.saveSeason')}
            </button>
          </div>
        )}
      </section>

      {/* Season lifecycle */}
      <section className="admin-section">
        <h3 className="admin-section-title">{t('leagues.seasonHeading')}</h3>
        <div className="admin-actions">
          {season && season.status === 'upcoming' && (
            <button className="btn-primary-sm" onClick={activateSeason}>{t('leagues.activateSeason')}</button>
          )}
          {season && season.status === 'active' && (
            <button className="btn-subtle" onClick={closeSeason}>{t('leagues.closeSeason')}</button>
          )}
          {season && season.playoff_tournament_id ? (
            <button
              className="btn-subtle"
              onClick={() => navigate(`/tournaments/${season.playoff_tournament_id}`)}
            >
              {t('leagues.viewPlayoff')}
            </button>
          ) : (
            season && season.status !== 'upcoming' && (
              <button className="btn-subtle" onClick={createPlayoff}>{t('leagues.createPlayoff')}</button>
            )
          )}
          <button className="btn-subtle" onClick={() => setSeasonOpen((v) => !v)}>
            {seasonOpen ? t('matchDetail.cancel') : t('leagues.newSeason')}
          </button>
          {season && (
            <button className="btn-subtle fixture-delete" onClick={deleteSeason}>{t('leagues.deleteSeason')}</button>
          )}
        </div>

        {seasonOpen && (
          <div className="admin-form-grid">
            <label className="form-field">
              <span className="form-label">{t('leagues.divisions')}</span>
              <input className="form-input" type="number" min="1" value={nsDivisions}
                onChange={(e) => setNsDivisions(e.target.value)} />
            </label>
            <label className="form-field">
              <span className="form-label">{t('leagues.slotsPerDivision')}</span>
              <input className="form-input" type="number" min="1" value={nsCapacity}
                onChange={(e) => setNsCapacity(e.target.value)} placeholder="∞" />
            </label>
            <label className="form-field">
              <span className="form-label">{t('leagues.promotion')}</span>
              <input className="form-input" type="number" min="0" value={nsPromote}
                onChange={(e) => setNsPromote(e.target.value)} />
            </label>
            <label className="form-field">
              <span className="form-label">{t('leagues.relegation')}</span>
              <input className="form-input" type="number" min="0" value={nsRelegate}
                onChange={(e) => setNsRelegate(e.target.value)} />
            </label>
            <button className="btn-primary-sm admin-form-submit" onClick={createSeason}>{t('leagues.createSeason')}</button>
          </div>
        )}
      </section>

      {/* Division structure — editable only before the season is activated */}
      {season && season.status === 'upcoming' && (
        <section className="admin-section">
          <h3 className="admin-section-title">{t('leagues.divisions')}</h3>
          <p className="form-hint">
            {t('leagues.divisionsHint')}
          </p>
          <div className="admin-actions">
            <span className="league-divisions-summary">
              {divisions.length === 1
                ? t('leagues.divisionCountOne', { count: divisions.length })
                : t('leagues.divisionCountMany', { count: divisions.length })}
              {divisions[0]?.capacity
                ? t('leagues.slotsSuffix', { count: divisions[0].capacity })
                : t('leagues.unlimitedSuffix')}
            </span>
            <button className="btn-subtle" onClick={() => setDivEditOpen((v) => !v)}>
              {divEditOpen ? t('matchDetail.cancel') : t('leagues.editDivisions')}
            </button>
          </div>
          {divEditOpen && (
            <div className="admin-form-grid">
              <label className="form-field">
                <span className="form-label">{t('leagues.divisionCount')}</span>
                <input className="form-input" type="number" min="1" value={edDivisions}
                  onChange={(e) => setEdDivisions(e.target.value)} />
              </label>
              <label className="form-field">
                <span className="form-label">{t('leagues.slotsPerDivision')}</span>
                <input className="form-input" type="number" min="1" value={edCapacity}
                  onChange={(e) => setEdCapacity(e.target.value)} placeholder="∞" />
              </label>
              <div className="form-field form-field-wide">
                <span className="form-label">{t('leagues.divisionNames')}</span>
                <div className="league-division-name-inputs">
                  {edNames.map((nm, i) => (
                    <input
                      key={i}
                      className="form-input"
                      value={nm}
                      aria-label={t('leagues.divisionNameAria', { n: i + 1 })}
                      placeholder={`Division ${i + 1}`}
                      onChange={(e) =>
                        setEdNames((prev) => prev.map((v, j) => (j === i ? e.target.value : v)))
                      }
                    />
                  ))}
                </div>
              </div>
              <button className="btn-primary-sm admin-form-submit" onClick={saveDivisions}>
                {t('leagues.saveDivisions')}
              </button>
            </div>
          )}
          {divisions.length > 0 && (
            <ul className="league-divisions-list">
              {divisions.map((d) => (
                <li key={d.id} className="league-divisions-row">
                  <span className="league-divisions-name">{d.name}</span>
                  <span className="league-divisions-count">
                    {d.participants?.length || 0}
                    {d.capacity ? ` / ${d.capacity}` : ''} {t('leagues.participantsWord')}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {season && (
        <>
          {/* Registration */}
          <section className="admin-section">
            <h3 className="admin-section-title">{t('leagues.registerParticipants')}</h3>
            {divisions.length > 1 && (
              <label className="form-field">
                <span className="form-label">{t('leagues.divisionOptional')}</span>
                <select className="form-input" value={regDivisionId}
                  onChange={(e) => setRegDivisionId(e.target.value)}>
                  <option value="">{t('leagues.lowestDivision')}</option>
                  {divisions.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </label>
            )}

            {teamBased ? (
              <div className="admin-actions">
                <select className="form-input" value={regTeamId} onChange={(e) => setRegTeamId(e.target.value)}>
                  <option value="">{t('leagues.selectTeam')}</option>
                  {teams.map((tm) => (
                    <option key={tm.id} value={tm.id} disabled={registeredTeamIds.has(tm.id)}>
                      {tm.name}{registeredTeamIds.has(tm.id) ? t('leagues.registeredSuffix') : ''}
                    </option>
                  ))}
                </select>
                <button className="btn-primary-sm" onClick={registerParticipant}>{t('leagues.registerTeamBtn')}</button>
              </div>
            ) : (
              <>
                {regPlayers.length > 0 && (
                  <div className="roster-chips">
                    <div className="roster-chip">
                      <span className="chip-name">{playerName(regPlayers[0])}</span>
                      <button type="button" className="chip-remove" onClick={() => setRegPlayers([])}>×</button>
                    </div>
                    <button className="btn-primary-sm" onClick={registerParticipant}>{t('leagues.registerPlayerBtn')}</button>
                  </div>
                )}
                <LeaguePlayerPicker
                  guildId={guildId}
                  selected={regPlayers}
                  onToggle={(p) => setRegPlayers((prev) =>
                    prev[0]?.player_id === p.player_id ? [] : [p])}
                  maxPlayers={1}
                />
              </>
            )}

            {allParticipants.length > 0 && (
              <div className="participant-admin-list">
                <span className="form-label">{t('leagues.registeredCount', { count: allParticipants.length })}</span>
                <div className="roster-chips">
                  {allParticipants.map((p) => (
                    <div key={p.id} className="roster-chip">
                      {p.team?.color && (
                        <span className="team-color-dot" style={{ '--team-color': p.team.color }} />
                      )}
                      <span className="chip-name">{p.display_name}</span>
                      {p.status === 'withdrawn' ? (
                        <span className="chip-withdrawn" title={t('leagues.withdrawn')}>⊘</span>
                      ) : p.played > 0 ? (
                        <button
                          type="button"
                          className="chip-remove"
                          title={t('leagues.withdrawTitle')}
                          onClick={() => withdrawParticipant(p)}
                        >⊘</button>
                      ) : (
                        <button
                          type="button"
                          className="chip-remove"
                          title={t('leagues.removeFromSeasonTitle')}
                          onClick={() => removeParticipant(p)}
                        >×</button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* Fixtures */}
          <section className="admin-section">
            <h3 className="admin-section-title">{t('leagues.fixtures')}</h3>

            <div className="admin-actions fixture-generate">
              <span className="form-label">{t('leagues.roundRobin')}</span>
              <label className="meetings-field">
                {t('leagues.meet')}
                <input
                  className="score-input"
                  type="number"
                  min="1"
                  value={fxMeetings}
                  onChange={(e) => setFxMeetings(e.target.value)}
                />
                {t('leagues.times')}
              </label>
              <button className="btn-primary-sm" onClick={generateFixtures}>
                {fixtures.length > 0 ? t('leagues.regenerateSchedule') : t('leagues.generateSchedule')}
              </button>
            </div>

            {fixtures.length > 0 && (
              <div className="fixture-progress">
                {t('leagues.fixturesPlayed', { done: completedCount, total: fixtures.length })}
                {completedCount === fixtures.length && t('leagues.allMatchesDone')}
              </div>
            )}

            <span className="form-label fixture-manual-label">{t('leagues.orCreateSingle')}</span>
            <div className="admin-form-grid admin-fixture-form">
              {divisions.length > 1 && (
                <label className="form-field">
                  <span className="form-label">{t('leagues.division')}</span>
                  <select className="form-input" value={fxDivisionId}
                    onChange={(e) => { setFxDivisionId(e.target.value); setFxHomeId(''); setFxAwayId(''); }}>
                    <option value="">{t('leagues.selectDivision')}</option>
                    {divisions.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </label>
              )}
              <label className="form-field">
                <span className="form-label">{t('leagues.home')}</span>
                <select className="form-input" value={fxHomeId} onChange={(e) => setFxHomeId(e.target.value)}>
                  <option value="">{t('leagues.selectEllipsis')}</option>
                  {fxParticipants.map((p) => <option key={p.id} value={p.id}>{p.display_name}</option>)}
                </select>
              </label>
              <label className="form-field">
                <span className="form-label">{t('leagues.awayTitle')}</span>
                <select className="form-input" value={fxAwayId} onChange={(e) => setFxAwayId(e.target.value)}>
                  <option value="">{t('leagues.selectEllipsis')}</option>
                  {fxParticipants.map((p) => <option key={p.id} value={p.id}>{p.display_name}</option>)}
                </select>
              </label>
              <button className="btn-primary-sm admin-form-submit" onClick={createFixture}>{t('leagues.createFixture')}</button>
            </div>

            <div className="fixture-list">
              {fixtures.length === 0 ? (
                <div className="league-empty">{t('leagues.noFixtures')}</div>
              ) : (
                fixtures.map((f) => (
                  <FixtureRow
                    key={f.id}
                    fixture={f}
                    onResult={recordResult}
                    onLink={linkMatch}
                    onUnlink={unlinkMatch}
                    onDelete={deleteFixture}
                    onForfeit={forfeitFixture}
                  />
                ))
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
